/**
 * @file Footer.jsx
 * @description Modern footer with Tailwind CSS
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import IMAGES from '../config/images';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [showFullDescription, setShowFullDescription] = useState(false);

  const quickLinks = [
    { label: 'Home', href: '/' },
    { label: 'Services', href: '/services' },
    { label: 'Projects', href: '/projects' },
    { label: 'Get a Quote', href: '/get-quote' },
    { label: 'Emergency Services', href: '/emergency' },
  ];

  const services = [
    'Plumbing Services',
    'Gas Services',
    'Heating Services',
    'Boiler Installation',
    'Bathroom Installation',
    'Emergency Repairs'
  ];

  const socialLinks = [
    { label: 'Facebook', icon: <FacebookIcon />, href: 'FACEBOOK_URL_HERE' },
    { label: 'Instagram', icon: <InstagramIcon />, href: 'INSTAGRAM_URL_HERE' },
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-10 right-10 w-40 h-40 bg-primary-500 rounded-full opacity-5 animate-pulse-slow"></div>
      <div className="absolute bottom-10 left-10 w-32 h-32 bg-primary-600 rounded-full opacity-5 animate-bounce-slow"></div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div className="animate-fade-in-up">
            <img 
              src={IMAGES.logo} 
              alt="Metro Plumbing & Heating" 
              className="h-12 w-auto mb-4 rounded-md"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <h5 className="text-xl font-bold text-primary-400 mb-4">
              Metro Plumbing & Heating
            </h5>
            <div className="text-gray-300 mb-4 leading-relaxed">
              <p className="mb-2">
                We have many years of experience in the plumbing and heating industry. We pride ourselves on excellent customer service and guarantee all our work to the highest standards.
              </p>
              {showFullDescription && (
                <p className="mb-2 animate-fade-in">
                  Open 24/7, we are here when you need us—especially for emergency appointments when the unexpected happens. We specialise in general plumbing, heating system installation, radiator leak repairs, and power flushing services. Whatever your plumbing needs, give us a call. We also offer quotes and professional advice.
                </p>
              )}
              <button
                onClick={() => setShowFullDescription(!showFullDescription)}
                className="text-primary-400 hover:text-primary-300 text-sm font-medium transition-colors duration-300 mt-1"
              >
                {showFullDescription ? '← Read Less' : 'Read More →'}
              </button>
            </div>
          
          </div>

          {/* Quick Links */}
          <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <h5 className="text-lg font-bold text-primary-400 mb-4">Quick Links</h5>
            <ul className="space-y-2">
              {quickLinks.map(({ label, href }) => (
                <li key={label}>
                  <Link
                    to={href}
                    className="text-gray-300 hover:text-primary-400 transition-colors duration-300 flex items-center group"
                  >
                    <span className="mr-2 transform group-hover:translate-x-1 transition-transform duration-300">›</span>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <h5 className="text-lg font-bold text-primary-400 mb-4">Our Services</h5>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service}>
                  <Link
                    to="/services"
                    className="text-gray-300 hover:text-primary-400 transition-colors duration-300 flex items-center group"
                  >
                    <span className="mr-2 transform group-hover:translate-x-1 transition-transform duration-300">›</span>
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Find Us - Google Map */}
          <div className="animate-fade-in-up relative z-10" style={{ animationDelay: '0.3s' }}>
            <h5 className="text-lg font-bold text-primary-400 mb-4">Find Us</h5>
            <div className="rounded-lg overflow-hidden shadow-lg h-64 bg-gray-700 relative z-10">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d19834.310921372708!2d-0.255614!3d51.581269000000006!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487611ceb9b6bd7d%3A0x93abe5c8265e66db!2sMETRO%20PLUMBING%20AND%20HEATING!5e0!3m2!1sen!2suk!4v1766347874655!5m2!1sen!2suk"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Metro Plumbing & Heating Location"
              ></iframe>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Serving North West London and surrounding areas
            </p>
          </div>
        </div>

        {/* Contact Us Section - Horizontal */}
        <div className="border-t border-gray-700 pt-8 mb-8">
          <div className="animate-fade-in-up">
            <h5 className="text-lg md:text-xl font-bold text-primary-400 mb-6 text-center">Contact Us</h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 max-w-6xl mx-auto px-4">
              <div className="flex flex-col items-center text-center space-y-3 bg-gray-800/50 rounded-lg p-5 md:p-6 hover:bg-gray-800 hover:shadow-xl hover:shadow-primary-500/20 hover:-translate-y-1 transition-all duration-300 transform cursor-pointer sm:col-span-2 lg:col-span-1">
                <PhoneIcon className="text-primary-400 transition-transform duration-300 group-hover:scale-110" fontSize="large" />
                <a href="tel:+447379787766" className="text-gray-300 hover:text-primary-400 transition-colors duration-300 font-medium text-base md:text-lg">
                  07379 787766
                </a>
              </div>
              <div className="flex flex-col items-center text-center space-y-3 bg-gray-800/50 rounded-lg p-5 md:p-6 hover:bg-gray-800 hover:shadow-xl hover:shadow-primary-500/20 hover:-translate-y-1 transition-all duration-300 transform cursor-pointer sm:col-span-2 lg:col-span-1">
                <EmailIcon className="text-primary-400 transition-transform duration-300 group-hover:scale-110" fontSize="large" />
                <a href="mailto:info@metroplumbingandheating.co.uk" className="text-gray-300 hover:text-primary-400 transition-colors duration-300 break-words font-medium text-xs sm:text-sm px-2">
                  info@metroplumbingandheating.co.uk
                </a>
              </div>
              <div className="flex flex-col items-center text-center space-y-3 bg-gray-800/50 rounded-lg p-5 md:p-6 hover:bg-gray-800 hover:shadow-xl hover:shadow-primary-500/20 hover:-translate-y-1 transition-all duration-300 transform sm:col-span-2 lg:col-span-1">
                <LocationOnIcon className="text-primary-400 transition-transform duration-300 group-hover:scale-110" fontSize="large" />
                <span className="text-gray-300 font-medium text-base md:text-lg">London, UK</span>
              </div>
            </div>
            
            {/* Social Links */}
            <div className="mt-6 text-center">
              <h6 className="text-sm font-semibold text-gray-400 mb-3">Follow Us</h6>
              <div className="flex justify-center space-x-3">
                {socialLinks.map(({ label, icon, href }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-700 hover:bg-primary-600 text-white transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg"
                  >
                    {icon}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm text-center">
              &copy; {currentYear} Metro Plumbing & Heating. All rights reserved.
            </p>
            <a
              href="https://www.jmvl.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-400 hover:text-primary-300 transition-colors duration-300 text-sm"
            >
            Powered by JMVL
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
